/**
 * Utilidades para el manejo y formateo de fechas
 */

import { useMemo } from 'react';

/**
 * Formatea una fecha ISO a formato local español con zona horaria
 * @param isoDate - Fecha en formato ISO string
 * @param includeTime - Si incluir la hora (por defecto true)
 * @returns Fecha formateada en español
 */
export const formatearFechaLocal = (isoDate: string, includeTime: boolean = true): string => {
  try {
    const fecha = new Date(isoDate);
    
    // Verificar si la fecha es válida
    if (isNaN(fecha.getTime())) {
      return 'Fecha no válida';
    }

    const opciones: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    if (includeTime) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
      opciones.second = '2-digit';
      opciones.hour12 = false; // Formato 24 horas
    }

    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return 'Error en fecha';
  }
};

/**
 * Formatea una fecha ISO a formato corto (dd/mm/yyyy hh:mm)
 * @param isoDate - Fecha en formato ISO string o "YYYY-MM-DD HH:MM:SS"
 * @returns Fecha formateada en formato corto
 */
export const formatearFechaCorta = (isoDate: string): string => {
  try {
    // Parsear la fecha interpretándola correctamente como hora de Madrid si es necesario
    const fecha = parsearFechaMadrid(isoDate);
    
    if (isNaN(fecha.getTime())) {
      return 'Fecha no válida';
    }

    // Obtener componentes de fecha en zona horaria de Madrid
    const opciones: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Madrid',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    
    const partes = new Intl.DateTimeFormat('es-ES', opciones).formatToParts(fecha);
    const dia = partes.find(p => p.type === 'day')?.value || '00';
    const mes = partes.find(p => p.type === 'month')?.value || '00';
    const año = partes.find(p => p.type === 'year')?.value || '0000';
    const hora = partes.find(p => p.type === 'hour')?.value || '00';
    const minuto = partes.find(p => p.type === 'minute')?.value || '00';
    
    return `${dia}/${mes}/${año}, ${hora}:${minuto}`;
  } catch (error) {
    console.error('Error al formatear fecha corta:', error);
    return 'Error en fecha';
  }
};

/**
 * Formatea una fecha para mostrar solo la fecha sin hora
 * @param isoDate - Fecha en formato ISO string
 * @returns Fecha formateada sin hora
 */
export const formatearSoloFecha = (isoDate: string): string => {
  return formatearFechaLocal(isoDate, false);
};

/**
 * Obtiene la fecha actual en zona horaria de Madrid
 * @returns Fecha actual formateada
 */
export const obtenerFechaActualMadrid = (): string => {
  const ahora = new Date();
  return formatearFechaLocal(ahora.toISOString());
};

/**
 * Genera una fecha ISO en zona horaria de Madrid
 * Esta función debe usarse para TODAS las operaciones de creación/modificación
 * @returns Fecha en formato ISO pero ajustada a la zona horaria de Madrid
 */
export const generarFechaISOMadrid = (): string => {
  const ahora = new Date();
  
  // Obtener fecha/hora en Madrid usando Intl API con formato específico
  // Usamos 'en-US' con formato numérico para evitar problemas de localización
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const partes = formatter.formatToParts(ahora);
  
  // Construir fecha ISO manualmente con los componentes de Madrid
  const year = partes.find(p => p.type === 'year')?.value || '2000';
  const month = partes.find(p => p.type === 'month')?.value || '01';
  const day = partes.find(p => p.type === 'day')?.value || '01';
  const hour = partes.find(p => p.type === 'hour')?.value || '00';
  const minute = partes.find(p => p.type === 'minute')?.value || '00';
  const second = partes.find(p => p.type === 'second')?.value || '00';
  
  // Formato: YYYY-MM-DD HH:MM:SS (formato compatible con SQL)
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

/**
 * Cache para offsets de zona horaria (evita recalcular para cada fecha)
 */
const timezoneOffsetCache = new Map<string, number>();

/**
 * Obtiene el offset de zona horaria de Madrid para una fecha específica
 * OPTIMIZADO: Usa cálculo directo basado en reglas DST de la UE
 * @param year - Año
 * @param month - Mes (1-12)
 * @param day - Día
 * @returns Offset en horas (1 o 2 para Madrid)
 */
const getMadridTimezoneOffset = (year: number, month: number, day: number): number => {
  // Crear una clave única para la fecha
  const dateKey = `${year}-${month}-${day}`;
  
  // Verificar cache
  if (timezoneOffsetCache.has(dateKey)) {
    return timezoneOffsetCache.get(dateKey)!;
  }
  
  // Calcular si es horario de verano (DST) en Madrid
  // Regla DST UE: último domingo de marzo a último domingo de octubre
  // Para simplificar y mejorar rendimiento, usamos una aproximación:
  // Abril-Octubre: CEST (UTC+2), resto: CET (UTC+1)
  // Esto es correcto para la mayoría de los casos y mucho más rápido
  
  let offset = 1; // Por defecto CET (UTC+1)
  
  if (month >= 4 && month <= 10) {
    // Abril a octubre: probablemente CEST (UTC+2)
    // Verificar casos límite: marzo y octubre pueden tener fechas en ambos horarios
    if (month === 3) {
      // Marzo: verificar si es después del último domingo (aproximación: después del día 25)
      offset = day > 25 ? 2 : 1;
    } else if (month === 10) {
      // Octubre: verificar si es antes del último domingo (aproximación: antes del día 25)
      offset = day < 25 ? 2 : 1;
    } else {
      // Abril-Septiembre: definitivamente CEST
      offset = 2;
    }
  }
  
  // Guardar en cache (limitar tamaño del cache a 500 entradas para ahorrar memoria)
  if (timezoneOffsetCache.size > 500) {
    const firstKey = timezoneOffsetCache.keys().next().value;
    if (firstKey) {
      timezoneOffsetCache.delete(firstKey);
    }
  }
  timezoneOffsetCache.set(dateKey, offset);
  
  return offset;
};

/**
 * Parsea una fecha guardada en formato "YYYY-MM-DD HH:MM:SS" interpretándola como hora de Madrid
 * y la convierte a un objeto Date correcto
 * OPTIMIZADO: Elimina el loop y calcula el offset directamente
 * @param fechaStr - Fecha en formato "YYYY-MM-DD HH:MM:SS" (sin indicador de zona horaria)
 * @returns Objeto Date correcto
 */
const parsearFechaMadrid = (fechaStr: string): Date => {
  try {
    // Si la fecha tiene 'T' o 'Z', es formato ISO estándar
    if (fechaStr.includes('T') || fechaStr.includes('Z')) {
      return new Date(fechaStr);
    }
    
    // Si es formato "YYYY-MM-DD HH:MM:SS" (sin indicador de zona horaria)
    // Interpretamos que es hora de Madrid y la convertimos correctamente
    const match = fechaStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (match) {
      const [, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr] = match;
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);
      const day = parseInt(dayStr);
      const hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      const second = parseInt(secondStr);
      
      // Obtener el offset de zona horaria (cacheado para mejor rendimiento)
      const offset = getMadridTimezoneOffset(year, month, day);
      
      // Crear la fecha en UTC ajustando por el offset
      return new Date(Date.UTC(
        year,
        month - 1,
        day,
        hour - offset,
        minute,
        second
      ));
    }
    
    // Fallback: intentar parsear directamente
    return new Date(fechaStr);
  } catch (error) {
    console.error('Error al parsear fecha de Madrid:', error);
    // Si falla, intentar parsear como fecha local (comportamiento anterior)
    return new Date(fechaStr);
  }
};

/**
 * Convierte una fecha ISO a formato legible para el historial
 * @param isoDate - Fecha en formato ISO string o "YYYY-MM-DD HH:MM:SS"
 * @returns Fecha formateada para historial
 */
export const formatearFechaHistorial = (isoDate: string): string => {
  try {
    // Parsear la fecha interpretándola correctamente como hora de Madrid si es necesario
    const fecha = parsearFechaMadrid(isoDate);
    
    if (isNaN(fecha.getTime())) {
      return 'Fecha no válida';
    }

    const ahora = new Date();
    const diferencia = ahora.getTime() - fecha.getTime();
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));

    // Si es hoy, mostrar solo la hora
    if (dias === 0) {
      return `Hoy a las ${fecha.toLocaleTimeString('es-ES', {
        timeZone: 'Europe/Madrid',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}`;
    }
    
    // Si es ayer
    if (dias === 1) {
      return `Ayer a las ${fecha.toLocaleTimeString('es-ES', {
        timeZone: 'Europe/Madrid',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}`;
    }
    
    // Si es esta semana (menos de 7 días)
    if (dias < 7) {
      return `${fecha.toLocaleDateString('es-ES', {
        timeZone: 'Europe/Madrid',
        weekday: 'long'
      })} a las ${fecha.toLocaleTimeString('es-ES', {
        timeZone: 'Europe/Madrid',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}`;
    }
    
    // Para fechas más antiguas, mostrar fecha completa
    return formatearFechaCorta(isoDate);
  } catch (error) {
    console.error('Error al formatear fecha del historial:', error);
    return formatearFechaCorta(isoDate);
  }
};

/**
 * Cache para resultados de formateo de fechas (evita recalcular)
 */
const dateFormatCache = new Map<string, string>();
const MAX_CACHE_SIZE = 1000;

/**
 * Formatea una fecha a formato legible DD/MM/YYYY - HH:MM:SS
 * Función unificada que reemplaza las múltiples versiones en diferentes componentes
 * @param fechaString - Fecha en cualquier formato
 * @returns Fecha formateada en formato DD/MM/YYYY - HH:MM:SS
 */
export const formatearFechaCompleta = (fechaString: string): string => {
  try {
    // Si ya está en el formato correcto, devolverla sin cambios
    if (/^\d{1,2}\/\d{1,2}\/\d{4} - \d{1,2}:\d{1,2}:\d{1,2}$/.test(fechaString)) {
      return fechaString;
    }
    
    // Si es formato "YYYY-MM-DD HH:MM:SS" (sin T ni Z), usar formatearFechaCorta
    // que interpreta correctamente como hora de Madrid
    if (fechaString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/)) {
      const fechaCorta = formatearFechaCorta(fechaString);
      // Extraer los segundos de la fecha original
      const segundos = fechaString.match(/\s+\d{2}:(\d{2}):(\d{2})$/);
      const segundosStr = segundos ? segundos[2] : '00';
      return fechaCorta.replace(', ', ' - ') + ':' + segundosStr;
    }
    
    // Parsear la fecha
    let fecha: Date;
    
    if (fechaString.includes('T') || fechaString.includes('Z')) {
      // Formato ISO estándar
      fecha = new Date(fechaString);
    } else if (/^\d{4}-\d{1,2}-\d{1,2}/.test(fechaString)) {
      // Formato ISO sin T: "2025-10-09 18:00:25"
      fecha = new Date(fechaString.replace(' ', 'T'));
    } else if (fechaString.includes(',')) {
      // Formato con coma: "7/10/2025, 14:19:39" (formato español: día/mes/año)
      const [fechaParte, horaParte] = fechaString.split(',');
      const [dia, mes, anio] = fechaParte.trim().split('/');
      fecha = new Date(`${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}T${horaParte.trim()}`);
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(fechaString)) {
      // Formato D/M/YYYY o DD/MM/YYYY sin hora (formato español)
      const [dia, mes, anio] = fechaString.split('/');
      fecha = new Date(`${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`);
    } else {
      // Intentar parsear directamente como último recurso
      fecha = new Date(fechaString);
    }
    
    if (isNaN(fecha.getTime())) {
      console.warn('Fecha inválida:', fechaString);
      return fechaString;
    }
    
    // Formatear en zona horaria de Madrid
    const opciones: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    
    const partes = new Intl.DateTimeFormat('es-ES', opciones).formatToParts(fecha);
    const dia = partes.find(p => p.type === 'day')?.value || '00';
    const mes = partes.find(p => p.type === 'month')?.value || '00';
    const anio = partes.find(p => p.type === 'year')?.value || '0000';
    const horas = partes.find(p => p.type === 'hour')?.value || '00';
    const minutos = partes.find(p => p.type === 'minute')?.value || '00';
    const segundos = partes.find(p => p.type === 'second')?.value || '00';
    
    return `${dia}/${mes}/${anio} - ${horas}:${minutos}:${segundos}`;
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return fechaString;
  }
};

/**
 * Hook memoizado para formatear fechas en componentes React
 * @param isoDate - Fecha en formato ISO string
 * @param formatter - Función de formateo a usar (por defecto formatearFechaCompleta)
 * @returns Fecha formateada (memoizada)
 */
export const useFormattedDate = (
  isoDate: string | null | undefined,
  formatter: (date: string) => string = formatearFechaCompleta
): string => {
  return useMemo(() => {
    if (!isoDate) {
      return 'Fecha no disponible';
    }
    
    // Verificar cache
    const formatterName = formatter.name || 'formatter';
    const cacheKey = `${formatterName}_${isoDate}`;
    if (dateFormatCache.has(cacheKey)) {
      return dateFormatCache.get(cacheKey)!;
    }
    
    // Formatear y guardar en cache
    const formatted = formatter(isoDate);
    
    // Limitar tamaño del cache
    if (dateFormatCache.size >= MAX_CACHE_SIZE) {
      const firstKey = dateFormatCache.keys().next().value;
      if (firstKey) {
        dateFormatCache.delete(firstKey);
      }
    }
    
    dateFormatCache.set(cacheKey, formatted);
    return formatted;
  }, [isoDate, formatter]);
};
